import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, CircularProgress, Stack, Toolbar } from '@mui/material';
import PortalIFrame from 'components/shared/PortalIFrame';
import VoiceTranscriptionsImporter from './VoiceTranscriptionsImporter';
import { ArrowBack, ChecklistRtl, ExpandLess, ExpandMore, UploadFile } from '@mui/icons-material';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import VoiceTranscriptionJobStatusViewer from 'components/VoiceTranscriptions/VoiceTranscriptionJobStatusViewer';
import ReportPlaceholderWarning from '../ReportPlaceholderWarning';
import useVoiceTranscriptionJobsForActivity from 'api-new/useVoiceTranscriptionJobsForActivity';
import LoadingPlaceholder from 'components/shared/LoadingPlaceholder';
import { useTranslation } from 'react-i18next';

const VoiceTranscriptionsReport = ({ focusGroupId, voiceTranscriptionsTabUri }) => {
    const { t } = useTranslation();
    const importVoiceTranscriptsFeatureEnabled = useFeatureIsOn('voice-transcriptions-import');
    const [isImportingTranscripts, setIsImportingTranscripts] = useState(false);
    const individualTranscriptStatusEnabled = useFeatureIsOn('voice-transcriptions-show-individual-status');
    const [isShowingIndividualStatus, setIsShowingIndividualStatus] = useState(false);
    const showToolbar = importVoiceTranscriptsFeatureEnabled || individualTranscriptStatusEnabled;

    const {
        data: transcriptionJobs,
        isLoading: isTranscriptionJobsLoading
    } = useVoiceTranscriptionJobsForActivity(focusGroupId);

    const isTranscriptionJobsProcessing = transcriptionJobs?.some(job => job.status === 'Processing');

    if (isTranscriptionJobsLoading) {
        return <LoadingPlaceholder />;
    }

    if (isImportingTranscripts) {
        return (
            <React.Fragment>
                <Stack gap={3}>
                    <Toolbar variant='dense' sx={{ backgroundColor: 'grey.10' }}>
                        <Button startIcon={<ArrowBack />} onClick={() => setIsImportingTranscripts(false)}>
                            Back
                        </Button>
                    </Toolbar>

                    <VoiceTranscriptionsImporter focusGroupId={focusGroupId} />
                </Stack>
            </React.Fragment>
        );
    }

    return (
        <Stack gap={2}>
            { showToolbar &&
                <Toolbar variant='dense' sx={{ backgroundColor: 'grey.10', justifyContent: 'flex-end' }}>
                    <Stack direction='row' gap={2}>
                        { importVoiceTranscriptsFeatureEnabled && 
                            <Button variant="outlined" startIcon={<UploadFile />} onClick={() => setIsImportingTranscripts(true)}>
                                Import Transcripts
                            </Button>
                        }
                        { individualTranscriptStatusEnabled && 
                            <Button variant="outlined" startIcon={<ChecklistRtl />} endIcon={isShowingIndividualStatus ? <ExpandLess /> : <ExpandMore /> } onClick={() => setIsShowingIndividualStatus(!isShowingIndividualStatus)}>
                                {t('reports.voiceTranscripts.processingStatus')}
                            </Button>
                        }
                    </Stack>
                </Toolbar>
            }

            { isShowingIndividualStatus && <VoiceTranscriptionJobStatusViewer activityId={focusGroupId} /> }
            
            {isTranscriptionJobsProcessing && 
                <Stack gap={5} alignItems='center' justifyContent='center' sx={{padding: 4, backgroundColor: 'grey.10', px: 4, py: 8, borderRadius: 2 }}>
                    <CircularProgress
                        size={120}
                        sx={{
                            animationDuration: '5s',
                            opacity: 0.7,
                        }}
                    />
                    
                    <ReportPlaceholderWarning
                        severity='info'
                        titleTranslationKey='reports.voiceTranscriptsProcessing'
                        bodyTranslationKey='reports.voiceTranscriptsProcessingExplanation'
                    />
                </Stack>
            }
            {!isTranscriptionJobsProcessing && <PortalIFrame
                containerProps={{ disableGutters: true }}
                id={'voiceTranscriptionsIframe'}
                frameInfo={{hideReportTabs: true}}
                relativeUrl={voiceTranscriptionsTabUri} />
            }
        </Stack>
    );
};

VoiceTranscriptionsReport.propTypes = {
    focusGroupId: PropTypes.string,
    voiceTranscriptionsTabUri: PropTypes.string
};

export default VoiceTranscriptionsReport;
